import React, { useEffect, useState } from "react";
import Modal from "../../../shared/components/Modal/Modal";
import formatBRL from "../../../shared/utils/formatBRL";
import {
  MAX_INSTALLMENTS,
  buildInitialSchedule,
  resizeSchedule,
  togglePaid,
  updateInstallment,
  isFullyPaid,
} from "../utils/paymentSchedule";
import "../../../component/admin/Product/Create.css";
import "./PaymentScheduleModal.css";

/**
 * Modal "Financeiro" da tela de Vendas: divide o pedido em até 6 parcelas
 * (vencimentos automáticos mês a mês) e deixa marcar cada uma como paga,
 * com a data do pagamento editável. Todas pagas → status Pago; senão Pendente.
 *
 * Props: isOpen, order, saving, onClose(), onSave(schedule)
 */
const PaymentScheduleModal = ({
  isOpen,
  order,
  saving = false,
  onClose,
  onSave,
}) => {
  const [schedule, setSchedule] = useState([]);

  useEffect(() => {
    if (isOpen && order) setSchedule(buildInitialSchedule(order));
  }, [isOpen, order]);

  if (!order) return null;

  const fullyPaid = isFullyPaid(schedule);
  const paidCents = schedule
    .filter((p) => p.paid)
    .reduce((sum, p) => sum + p.amount, 0);

  const footer = (
    <div className="payment-schedule__footer">
      <span
        className={`payment-schedule__status payment-schedule__status--${fullyPaid ? "paid" : "pending"}`}
        data-testid="payment-schedule-status"
      >
        {fullyPaid ? "Pago" : "Pendente"}
      </span>
      <div className="payment-schedule__actions">
        <button
          type="button"
          className="cp-btn cp-btn--secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="cp-btn cp-btn--primary"
          onClick={() => onSave(schedule)}
          disabled={saving || schedule.length === 0}
        >
          {saving ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Financeiro"
      size="lg"
      footer={footer}
    >
      <div className="payment-schedule">
        <div className="payment-schedule__summary">
          <div>
            <span className="payment-schedule__label">Cliente</span>
            <strong>{order.customer?.name || "—"}</strong>
          </div>
          <div>
            <span className="payment-schedule__label">Total</span>
            <strong>{formatBRL(order.amount / 100)}</strong>
          </div>
          <div>
            <span className="payment-schedule__label">Recebido</span>
            <strong>{formatBRL(paidCents / 100)}</strong>
          </div>
          <label className="payment-schedule__count">
            <span className="payment-schedule__label">Parcelas</span>
            <select
              aria-label="Quantidade de parcelas"
              value={schedule.length || 1}
              onChange={(e) =>
                setSchedule(
                  resizeSchedule(
                    schedule,
                    Number(e.target.value),
                    order.amount,
                  ),
                )
              }
            >
              {Array.from({ length: MAX_INSTALLMENTS }, (_, i) => i + 1).map(
                (n) => (
                  <option key={n} value={n}>
                    {n}x
                  </option>
                ),
              )}
            </select>
          </label>
        </div>

        <table className="payment-schedule__table">
          <thead>
            <tr>
              <th>Parcela</th>
              <th>Valor</th>
              <th>Vencimento</th>
              <th>Pago</th>
              <th>Data do pagamento</th>
            </tr>
          </thead>
          <tbody>
            {schedule.map((p, i) => (
              <tr key={p.number} className={p.paid ? "is-paid" : undefined}>
                <td>
                  {p.number}/{schedule.length}
                </td>
                <td>{formatBRL(p.amount / 100)}</td>
                <td>
                  <input
                    type="date"
                    aria-label={`Vencimento parcela ${p.number}`}
                    value={p.dueDate}
                    onChange={(e) =>
                      setSchedule(
                        updateInstallment(
                          schedule,
                          i,
                          "dueDate",
                          e.target.value,
                        ),
                      )
                    }
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Parcela ${p.number} paga`}
                    checked={p.paid}
                    onChange={() => setSchedule(togglePaid(schedule, i))}
                  />
                </td>
                <td>
                  <input
                    type="date"
                    aria-label={`Data do pagamento parcela ${p.number}`}
                    value={p.paidAt}
                    disabled={!p.paid}
                    onChange={(e) =>
                      setSchedule(
                        updateInstallment(
                          schedule,
                          i,
                          "paidAt",
                          e.target.value,
                        ),
                      )
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
};

export default PaymentScheduleModal;
